import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

// ── Helpers de fechas (todo en 'YYYY-MM-DD') ─────────────────

const DAY_MS = 86400000;
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const fromYmd = (s) =>
  new Date(parseInt(s.slice(0, 4)), parseInt(s.slice(5, 7)) - 1, parseInt(s.slice(8, 10)));

// Lunes de la semana que contiene d
const mondayOf = (d) => {
  const day = (d.getDay() + 6) % 7;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
};

const anyToYmd = (v) => {
  if (!v) return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const d = v?.seconds ? new Date(v.seconds * 1000) : new Date(v);
  return isNaN(d) ? null : ymd(d);
};

export default function AdminCalendarioReservas() {
  const { token } = useAuth();
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('month'); // 'month' | 'week'
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    Promise.all([api.getAdminEvents(token), api.getAdminBookings(token)])
      .then(([evRes, bkRes]) => {
        const evMap = new Map((evRes.events || []).map((e) => [e.id, e]));
        const list = [];
        for (const b of bkRes.bookings || []) {
          if (b.status === 'cancelled') continue;
          // Rango propio de la reserva; si no lo tiene, el de su evento
          let cin = anyToYmd(b.checkInDate);
          let cout = anyToYmd(b.checkOutDate);
          if (!cin && b.eventId) {
            const ev = evMap.get(b.eventId);
            cin = anyToYmd(ev?.date);
            cout = anyToYmd(ev?.dateEnd) || cin;
          }
          if (!cin) continue;
          list.push({ ...b, _in: cin, _out: cout || cin });
        }
        setReservas(list);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const shift = (dir) =>
    setCursor((c) =>
      view === 'month'
        ? new Date(c.getFullYear(), c.getMonth() + dir, 1)
        : new Date(c.getFullYear(), c.getMonth(), c.getDate() + dir * 7)
    );

  const title = useMemo(() => {
    if (view === 'month') {
      return cursor.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    }
    const mon = mondayOf(cursor);
    const sun = new Date(mon.getTime() + 6 * DAY_MS);
    const fmt = (d) => d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    return `${fmt(mon)} — ${fmt(sun)} ${sun.getFullYear()}`;
  }, [view, cursor]);

  const days = useMemo(() => {
    if (view === 'week') {
      const mon = mondayOf(cursor);
      return Array.from({ length: 7 }, (_, i) => new Date(mon.getTime() + i * DAY_MS));
    }
    const start = mondayOf(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
    const lastOfMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const out = [];
    for (let d = new Date(start); ; d = new Date(d.getTime() + DAY_MS)) {
      out.push(new Date(d));
      if (out.length % 7 === 0 && d >= lastOfMonth) break;
      if (out.length > 42) break;
    }
    return out;
  }, [view, cursor]);

  // Reservas cuya estancia toca el día (salida inclusive)
  const reservasDelDia = (dayStr) =>
    reservas
      .filter((r) => r._in <= dayStr && dayStr <= r._out)
      .sort((a, b) => a._in.localeCompare(b._in));

  const resumen = useMemo(() => {
    if (days.length === 0) return { reservas: 0, personas: 0 };
    const from = ymd(days[0]);
    const to = ymd(days[days.length - 1]);
    const vis = reservas.filter((r) => r._in <= to && from <= r._out);
    const personas = vis.reduce((s, r) => s + (r.totalMembers || r.members?.length || 1), 0);
    return { reservas: vis.length, personas };
  }, [reservas, days]);

  const todayStr = ymd(new Date());
  const nombre = (r) => r.familyName || r.members?.[0]?.name || r.contactEmail || '(sin nombre)';

  const chipStyle = (r, dayStr) => ({
    display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
    border: 'none', borderRadius: 4, padding: '.1rem .35rem',
    fontSize: '.68rem', fontWeight: 600, lineHeight: 1.35, fontFamily: "'DM Sans',sans-serif",
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    background: r._in === dayStr ? 'rgba(122,171,138,.28)' : r._out === dayStr ? 'rgba(245,158,11,.22)' : 'rgba(255,255,255,.08)',
    color: r._in === dayStr ? 'var(--sage)' : r._out === dayStr ? '#f5b04b' : 'var(--mid)',
  });

  const viewBtn = (v, label) => (
    <button
      className="btn btn-sm"
      onClick={() => setView(v)}
      style={{
        borderRadius: 0,
        background: view === v ? 'var(--fern)' : 'rgba(255,255,255,.05)',
        color: view === v ? 'var(--forest)' : 'var(--mid)',
        border: '1px solid rgba(122,171,138,.25)',
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="main-content page-enter">
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: '.8rem', marginBottom: '1.2rem' }}>
        <div>
          <div className="section-label">Administración</div>
          <h1>🗓 Calendario de reservas</h1>
          <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.9rem', marginTop: '.2rem' }}>
            {resumen.reservas} reservas · {resumen.personas} personas en el periodo visible
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex' }}>{viewBtn('month', 'Mes')}{viewBtn('week', 'Semana')}</div>
          <button className="btn btn-ghost btn-sm" onClick={() => shift(-1)}>‹</button>
          <span style={{ minWidth: 150, textAlign: 'center', fontWeight: 700, textTransform: 'capitalize', fontSize: '.9rem' }}>
            {title}
          </span>
          <button className="btn btn-ghost btn-sm" onClick={() => shift(1)}>›</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setCursor(new Date())}>Hoy</button>
        </div>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem' }}><span className="spinner spinner-lg" /></div>
      )}

      {!loading && view === 'month' && (
        <div style={{ overflowX: 'auto' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, minWidth: 860,
            background: 'rgba(122,171,138,.15)', border: '1px solid rgba(122,171,138,.15)', borderRadius: 'var(--radius)', overflow: 'hidden',
          }}>
            {WEEKDAYS.map((w) => (
              <div key={w} style={{ background: 'rgba(122,171,138,.08)', padding: '.4rem', textAlign: 'center', fontSize: '.62rem', fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--sage)' }}>
                {w}
              </div>
            ))}
            {days.map((d) => {
              const dayStr = ymd(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const rs = reservasDelDia(dayStr);
              const MAX = 3;
              return (
                <div
                  key={dayStr}
                  onClick={() => { setCursor(d); setView('week'); }}
                  title="Ver semana"
                  style={{ minHeight: 108, background: 'var(--card, rgba(20,32,24,.9))', padding: '.35rem', cursor: 'pointer', opacity: inMonth ? 1 : 0.4 }}
                >
                  <div style={{ textAlign: 'right', fontSize: '.7rem', fontWeight: 700, color: dayStr === todayStr ? 'var(--fern)' : 'var(--mid)', marginBottom: '.25rem' }}>
                    {dayStr === todayStr ? '● ' : ''}{d.getDate()}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {rs.slice(0, MAX).map((r) => (
                      <button key={r.id} style={chipStyle(r, dayStr)}
                        onClick={(e) => { e.stopPropagation(); setSelected(r); }}>
                        {r._in === dayStr ? '→ ' : r._out === dayStr ? '← ' : ''}
                        {nombre(r)}{(r.totalMembers || 0) > 1 ? ` ·${r.totalMembers}` : ''}
                      </button>
                    ))}
                    {rs.length > MAX && (
                      <div style={{ fontSize: '.66rem', color: 'var(--mid)', paddingLeft: '.35rem' }}>+{rs.length - MAX} más</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: '.5rem' }}>
            <span style={{ marginRight: '1rem' }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(122,171,138,.45)', marginRight: 4 }} />→ llegada</span>
            <span style={{ marginRight: '1rem' }}><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(245,158,11,.4)', marginRight: 4 }} />← salida</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'rgba(255,255,255,.15)', marginRight: 4 }} />estancia</span>
          </p>
        </div>
      )}

      {!loading && view === 'week' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
          {days.map((d) => {
            const dayStr = ymd(d);
            const rs = reservasDelDia(dayStr);
            return (
              <div key={dayStr} className="card" style={{
                display: 'flex', gap: '1rem', padding: '.8rem 1rem', alignItems: 'flex-start',
                border: dayStr === todayStr ? '1px solid var(--fern)' : undefined,
              }}>
                <div style={{ width: 130, flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '.9rem', textTransform: 'capitalize', color: dayStr === todayStr ? 'var(--fern)' : 'var(--white)' }}>
                    {d.toLocaleDateString('es-ES', { weekday: 'long' })}
                  </div>
                  <div style={{ fontSize: '.75rem', color: 'var(--mid)' }}>
                    {d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  {rs.length === 0 ? (
                    <div style={{ fontSize: '.82rem', color: 'var(--mid)', fontStyle: 'italic', padding: '.2rem 0' }}>Sin reservas</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
                      {rs.map((r) => (
                        <button key={r.id} onClick={() => setSelected(r)} style={{
                          display: 'flex', alignItems: 'center', gap: '.4rem', cursor: 'pointer',
                          borderRadius: 6, padding: '.25rem .55rem', fontSize: '.76rem', fontWeight: 600,
                          fontFamily: "'DM Sans',sans-serif",
                          background: r._in === dayStr ? 'rgba(122,171,138,.15)' : r._out === dayStr ? 'rgba(245,158,11,.12)' : 'rgba(255,255,255,.05)',
                          border: `1px solid ${r._in === dayStr ? 'rgba(122,171,138,.4)' : r._out === dayStr ? 'rgba(245,158,11,.35)' : 'rgba(255,255,255,.12)'}`,
                          color: 'var(--white)',
                        }}>
                          {r._in === dayStr && <span className="badge badge-green" style={{ fontSize: '.55rem', padding: '.05rem .3rem' }}>llegada</span>}
                          {r._out === dayStr && <span className="badge badge-amber" style={{ fontSize: '.55rem', padding: '.05rem .3rem' }}>salida</span>}
                          {nombre(r)}
                          <span style={{ color: 'var(--mid)' }}>👥 {r.totalMembers || r.members?.length || 1}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setSelected(null)}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <div>
                <div className="section-label">Reserva</div>
                <h2>{nombre(selected)}</h2>
              </div>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div style={{ fontSize: '.9rem', display: 'flex', flexDirection: 'column', gap: '.45rem' }}>
              {selected.eventName && <div><span style={{ color: 'var(--mid)' }}>Evento:</span> {selected.eventName}</div>}
              <div>
                <span style={{ color: 'var(--mid)' }}>Estancia:</span>{' '}
                {fromYmd(selected._in).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                {' → '}
                {fromYmd(selected._out).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
              </div>
              {selected.contactEmail && <div><span style={{ color: 'var(--mid)' }}>Contacto:</span> {selected.contactEmail}</div>}
              {(selected.members?.length ?? 0) > 0 && (
                <div>
                  <span style={{ color: 'var(--mid)' }}>Personas ({selected.totalMembers || selected.members.length}):</span>
                  <ul style={{ margin: '.3rem 0 0 1.2rem' }}>
                    {selected.members.map((m, i) => <li key={i}>{m.name || '(sin nombre)'}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
